import 'dayjs/locale/en-gb';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useFindDepartmentsLazy } from '@src/adapters/hooks/department/useFindDepartments';
import DepartmentDTO from '@src/adapters/dto/department/DepartmentDTO';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useSaveJobLazy from '@src/adapters/hooks/job/useSaveJob';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { ContractTypes } from '@src/constants/ContractType';
import dayjs from 'dayjs';
import DepartmentRoleDTO from '@src/adapters/dto/department/DepartmentRoleDTO';

interface Props {
  job?: JobDTO;
  open: boolean;
  productionId?: string;
  close(job: JobDTO | undefined): void;
}

function JobAddEdit({ job, open, productionId, close }: Props) {
  const { execute: save } = useSaveJobLazy();
  const { execute: getDepartments, loading } = useFindDepartmentsLazy();
  const [departments, setDepartments] = useState<DepartmentDTO[]>([]);
  const [roles, setRoles] = useState<DepartmentRoleDTO[]>([]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    resetField,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<JobDTO>({ defaultValues: job });

  const departmentId = watch('departmentId');

  const findDepartments = useCallback(() => {
    getDepartments().then((result) => {
      setDepartments(result.departments);
    });
  }, [getDepartments]);

  useEffect(() => {
    if (productionId) {
      setValue('productionId', productionId);
    }
  }, [productionId, setValue]);

  useEffect(() => {
    const department = departmentId
      ? departments.find((d) => d.id === departmentId)
      : null;
    resetField('role');
    setRoles(department ? department.roles.filter((r) => r.isActive) : []);
  }, [departmentId, departments, resetField]);

  useEffect(() => {
    findDepartments();
  }, [findDepartments]);

  const onSubmit = async (data: JobDTO) => {
    const result = await save({
      job: data,
    });
    if (result.job) {
      reset();
      close(result.job);
    }
  };

  const handleClose = () => {
    reset();
    close(undefined);
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="Note Details"
      aria-describedby="Note Details"
      fullWidth={true}
      maxWidth={'md'}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}>
          {productionId ? 'Add Job' : 'Edit Job'}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            width: '100%',
          }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Controller
                name="departmentId"
                control={control}
                defaultValue={undefined}
                rules={{ required: true }}
                render={({ field }) => {
                  const selectedDepartment =
                    departments.find((option) => option.id === field.value) ||
                    null;
                  return !productionId ? (
                    <TextField
                      fullWidth
                      label="Department"
                      variant="standard"
                      value={selectedDepartment?.name || ''}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      sx={{ paddingTop: '10px' }}
                    />
                  ) : (
                    <Autocomplete
                      fullWidth
                      onChange={(event, value) => {
                        field.onChange(value ? value.id : undefined);
                      }}
                      value={selectedDepartment}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      getOptionLabel={(option) => option.name || ''}
                      options={departments}
                      loading={loading}
                      filterSelectedOptions
                      sx={{ paddingTop: '10px' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.departmentId}
                          label="Department"
                          variant="standard"
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <Box>
                                  {errors.departmentId && (
                                    <PriorityHighIcon color="inherit" />
                                  )}
                                </Box>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="role"
                control={control}
                rules={{ required: 'Role is required' }}
                render={({ field }) => {
                  return !productionId ? (
                    <TextField
                      fullWidth
                      label="Role"
                      variant="standard"
                      value={getValues('role.name')}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      sx={{ paddingTop: '10px' }}
                    />
                  ) : (
                    <Autocomplete
                      fullWidth
                      onChange={(event, value) => {
                        field.onChange(value || undefined);
                      }}
                      // value={value || null}
                      getOptionLabel={(option) => option.name || ''}
                      options={roles}
                      isOptionEqualToValue={(option, value) => option === value}
                      sx={{ paddingTop: '10px' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Role"
                          variant="standard"
                          error={!!errors.role}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <Box>
                                  {errors.role && (
                                    <PriorityHighIcon color="inherit" />
                                  )}
                                </Box>
                              ),
                            },
                          }}
                        />
                      )}
                      disabled={!roles || roles.length === 0}
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="contractType"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    onChange={(event, value) => {
                      field.onChange(value || undefined);
                    }}
                    value={field.value || null}
                    getOptionLabel={(option) => option || ''}
                    options={ContractTypes}
                    isOptionEqualToValue={(option, value) => option === value}
                    sx={{ paddingTop: '10px' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contract type"
                        variant="standard"
                        error={!!errors.contractType}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                          },
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="shortName"
                control={control}
                rules={{ required: true }}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Short Name"
                    error={!!errors.shortName}
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.shortName && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb">
                <Controller
                  name="startDate"
                  control={control}
                  defaultValue={null}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      label="Start Date"
                      format="DD/MM/YYYY"
                      // disabled={!job && !job!.id}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? date.format('YYYY-MM-DD') : null);
                      }}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          // inputProps: {
                          //   readOnly: !job || !job.id,
                          // },
                          sx: {
                            width: '100%',
                            marginTop: '20px',
                            marginBottom: '20px',
                            '& .MuiFormLabel-root': {
                              color: '#878787',
                            },
                            '.MuiSvgIcon-root': {
                              color: '#878787',
                              WebkitTextFillColor: '#878787',
                            },
                            '& .MuiInputBase-input': {
                              color: '#878787',
                              WebkitTextFillColor: '#878787',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb">
                <Controller
                  name="endDate"
                  control={control}
                  defaultValue={null}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <DatePicker
                      label="End Date"
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => {
                        field.onChange(date ? date.format('YYYY-MM-DD') : null);
                      }}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          sx: {
                            width: '100%',
                            marginTop: '20px',
                            marginBottom: '20px',
                            '& .MuiFormLabel-root': {
                              color: '#878787',
                            },
                            '.MuiSvgIcon-root': {
                              color: '#878787',
                            },
                            '& .MuiInputBase-input': {
                              color: '#878787',
                              WebkitTextFillColor: '#878787',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <Controller
                name="contact.name"
                control={control}
                rules={{ required: true }}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Department Head"
                    error={!!errors.contact?.name}
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.contact && errors.contact.name && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="contact.phone"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Department Phone"
                    error={!!errors.contact?.phone}
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.contact && errors.contact.phone && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="contact.email"
                control={control}
                rules={{ required: true }}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Department Email"
                    error={!!errors.contact?.email}
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.contact && errors.contact.email && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="fee"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.fee}
                    label="Fee"
                    type="number"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.fee && <PriorityHighIcon color="inherit" />}
                          </Box>
                        ),
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="numberOfPositions"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Number of Positions"
                    type="number"
                    error={!!errors.numberOfPositions}
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.numberOfPositions && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Controller
                name="location"
                control={control}
                rules={{ required: true }}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Location"
                    sx={{
                      width: '100%',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                    error={!!errors.location}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <Box>
                            {errors.location && (
                              <PriorityHighIcon color="inherit" />
                            )}
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="additionalInfo"
                control={control}
                render={({ ...params }) => (
                  <TextField
                    {...params.field}
                    label="Additional Info"
                    multiline
                    rows={4}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default JobAddEdit;
